'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Book, BotMessageSquare, MessageSquare } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionStatus } from '../components/SubscriptionStatus';

interface UserData {
  signUpStatus: string;
  subscription: string;
}

const Dashboard = () => {
  const [signUpStatus, setSignUpStatus] = useState('');
  const [currentlySubscribed, setCurrentlySubscribed] = useState(false);
  const [onboardedDismissed, setOnboardedDismissed] = useState(false);
  const { apiCall } = useAuthenticatedApi();
  const { subscriptionSpend, subscriptionLimit } = useSubscription();
  const { toast } = useToast();

  const fetchUser = useCallback(async () => {
    try {
      const response = await apiCall<UserData>('/user', 'GET');

      if (response) {
        setCurrentlySubscribed(!['', 'cancelled', 'ending', 'trial'].includes(response.subscription));

        if (['onboarded', 'waiting'].includes(response.signUpStatus)) {
          setSignUpStatus(response.signUpStatus);
        }

        if (response.signUpStatus === 'onboarded') {
          localStorage.setItem('onboarded', 'true');
        }
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Failed to fetch user data',
        variant: 'destructive',
      });
    }
  }, [apiCall, toast]);

  useEffect(() => {
    fetchUser();
    const dismissed = localStorage.getItem('onboardedDismissed') === 'true';
    setOnboardedDismissed(dismissed);
  }, [fetchUser]);

  const handleOnboardedDismiss = () => {
    localStorage.setItem('onboardedDismissed', 'true');
    setOnboardedDismissed(true);
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {signUpStatus === 'waiting' && (
        <Alert className="mb-6">
          <AlertTitle>Welcome to Creative AI Writer!</AlertTitle>
          <AlertDescription>
            <p>We're thrilled to see so much interest in our platform.</p>
            <p>
              Due to high demand, we're staggering our onboarding to ensure the best experience for everyone. You're on
              our waiting list, and your spot is reserved!
            </p>
            <p>We'll notify you by email as soon as the next batch opens. Keep an eye on your inbox!</p>
          </AlertDescription>
        </Alert>
      )}

      {signUpStatus === 'onboarded' && !onboardedDismissed && (
        <Alert className="mb-6">
          <AlertTitle>Welcome aboard! 🎉</AlertTitle>
          <AlertDescription>
            <p>We're thrilled to have you as one of our early adopters!</p>
            <p>
              Take a look around and explore all the tools at your fingertips. If you have any questions or feedback,
              feel free to reach out.
            </p>
            <Button variant="outline" onClick={handleOnboardedDismiss} className="mt-2">
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
            <CardDescription>Create and manage your stories, speeches, or books</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/documents">
              <Button className="w-full">
                <Book className="mr-2 h-4 w-4" /> View Documents
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Character Document Chat</CardTitle>
            <CardDescription>Chat with characters from your stories</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/character-doc-chats">
              <Button className="w-full">
                <MessageSquare className="mr-2 h-4 w-4" /> Start Chatting
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>AI Chat</CardTitle>
            <CardDescription>Chat with characters from your stories</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/ai-chat">
              <Button className="w-full">
                <BotMessageSquare className="mr-2 h-4 w-4" /> Start an AI chat
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <SubscriptionStatus
        subscriptionSpend={subscriptionSpend}
        subscriptionLimit={subscriptionLimit}
        currentlySubscribed={currentlySubscribed}
      />
    </div>
  );
};

export default Dashboard;
